import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslate } from '@refinedev/core';
import React, { Suspense, lazy, useState } from 'react';
import { IMedia } from '@/interfaces';
import { MediaDialogSkeleton } from './MediaDialogSkeleton';
import { useFormContext } from 'react-hook-form-mui';

// Lazy load the Media component
const LazyMedia = lazy(() => import('@/pages/medias').then(module => ({
  default: module.Media
})));

export const MediaDialog = React.memo(({
  open,
  onClose,
  name,
  onAdd,
  allowMultiple = true,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  onAdd?: (medias: IMedia[]) => void;
  allowMultiple?: boolean;
}) => {
  const t = useTranslate();
  const { getValues } = useFormContext();
  const [selectedMedia, setSelectedMedia] = useState<IMedia[]>([]);

  // Get current media items from form
  const currentMediaItems = React.useMemo(() => {
    return getValues(name) || [];
  }, [getValues, name]);

  // Reset selection when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setSelectedMedia([]);
    }
  }, [open]);

  const handleMediaSelect = React.useCallback((medias: IMedia[]) => {
    setSelectedMedia(medias);
  }, []);

  const handleConfirmSelection = React.useCallback(() => {
    if (onAdd && selectedMedia.length > 0) {
      onAdd(selectedMedia);
    }
    setSelectedMedia([]);
    onClose();
  }, [selectedMedia, onAdd, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{
        m: 0,
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {t("medias.titles.list")}
        <IconButton
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {open && (
        <>
          <DialogContent sx={{ p: 0 }}>
            <Suspense fallback={<MediaDialogSkeleton />}>
              <LazyMedia
                enabledActions={{ archive: false, view: true }}
                allowMultiple={allowMultiple}
                onSelectionChange={handleMediaSelect}
                formFieldName={name}
                currentMediaItems={currentMediaItems}
              />
            </Suspense>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={onClose}>
              {t("buttons.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={selectedMedia.length === 0}
              onClick={handleConfirmSelection}
            >
              {t("buttons.add")} {selectedMedia.length > 0 ? `(${selectedMedia.length})` : ''}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}); 